import React, { useEffect, useState, useRef } from "react";
import "./ResourceActionsSidebar.css";
import { useSelector, useDispatch } from "react-redux";
import {
  selectLinkRecords,
  selectUIStates,
  setUIStates,
} from "../../redux/features/appData/appDataSlice";
import {
  LinkRecord,
  LinkRecordAction,
  LinkRecordActionType,
} from "winslow-core";
import { formatDate } from "winslow-core";
import {
  apiGetCollectionLinkRecordActions,
  apiGetLinkRecordActions,
} from "../../services/CollectionService";
import {
  CheckCircle,
  FolderSyncIcon,
  RefreshCw,
  SquareCheck,
  SquarePlus,
  SquareX,
} from "lucide-react";

interface ResourceActionsSidebarProps {
  collectionId: string;
  linkRecordId?: string;
}

export const ResourceActionsSidebar: React.FC<ResourceActionsSidebarProps> = ({
  collectionId,
  linkRecordId,
}) => {
  const [linkRecordActions, setLinkRecordActions] = useState<
    LinkRecordAction[]
  >([]);
  const actionMessagesRef = useRef<HTMLDivElement>(null);
  const dispatch = useDispatch();
  const uiStates = useSelector(selectUIStates);
  const linkRecords = useSelector(selectLinkRecords);

  const linkRecord = linkRecords?.find(
    (linkRecord) => linkRecord.id === linkRecordId
  );

  const onClose = () => {
    dispatch(setUIStates({ ...uiStates, resourceSidebarOpen: false }));
  };

  useEffect(() => {
    if (!uiStates.resourceSidebarOpen) {
      setLinkRecordActions([]);
      return;
    }

    if (uiStates.resourceSidebarOpen) {
      (async () => {
        if (linkRecordId) {
          const actions = await apiGetLinkRecordActions(linkRecordId);
          setLinkRecordActions(
            actions?.sort(
              (a, b) =>
                new Date(a.createdAt).getTime() -
                new Date(b.createdAt).getTime()
            ) || []
          );
        } else {
          const actions = await apiGetCollectionLinkRecordActions(collectionId);
          setLinkRecordActions(
            actions?.sort(
              (a, b) =>
                new Date(a.createdAt).getTime() -
                new Date(b.createdAt).getTime()
            ) || []
          );
        }
      })();
    }
  }, [uiStates.resourceSidebarOpen, linkRecordId, collectionId]);

  useEffect(() => {
    if (actionMessagesRef.current) {
      actionMessagesRef.current.scrollTop =
        actionMessagesRef.current.scrollHeight;
    }
  }, [linkRecordActions]);

  useEffect(() => {
    return () => {
      setLinkRecordActions([]);
    };
  }, []);

  if (!uiStates.resourceSidebarOpen) return null;

  const formatActionType = (actionType: LinkRecordActionType): string => {
    const typeMap: Record<LinkRecordActionType, string> = {
      [LinkRecordActionType.CREATED]: "Created",
      [LinkRecordActionType.UPDATED]: "Updated",
      [LinkRecordActionType.DELETED]: "Deleted",
      [LinkRecordActionType.CONNECTED]: "Connected",
      [LinkRecordActionType.EMBEDDED]: "Embedded",
    };
    return typeMap[actionType] || actionType;
  };

  return (
    <div className="resource-sidebar">
      <div className="resource-sidebar-body">
        <div className="resource-info">
          <div className="row resource-sidebar-header">
            <div className="resource-header">
              <div className="author-info">
                {linkRecord && (
                  <div className="resource-title">
                    {linkRecord.links?.[0]?.title || "Untitled Resource"}
                  </div>
                )}
              </div>
            </div>
            <div className="resource-sidebar-close" onClick={onClose}>
              <img
                className="close-icon"
                src="/icons/close-icon.svg"
                alt="Close"
              />
            </div>
          </div>

          <div className="action-messages" ref={actionMessagesRef}>
            {linkRecordActions.map((action) => {
              const actionLinkRecord = linkRecords?.find(
                (lr) => lr.id === action.linkRecordId
              );
              const actionTitle =
                actionLinkRecord?.links?.[0]?.title || "Untitled Resource";

              return (
                <div key={action.id} className="action-message">
                  <div className="action-message-header">
                    <div className="action-type-icon">
                      {action.actionType === LinkRecordActionType.CONNECTED && (
                        <SquarePlus style={{ color: "#FF543D" }} />
                      )}
                      {action.actionType === LinkRecordActionType.CREATED && (
                        <SquarePlus style={{ color: "#FF543D" }} />
                      )}
                      {action.actionType === LinkRecordActionType.UPDATED && (
                        <RefreshCw style={{ color: "#ffbe00" }} />
                      )}
                      {action.actionType === LinkRecordActionType.DELETED && (
                        <SquareX style={{ color: "#f61924" }} />
                      )}
                      {action.actionType === LinkRecordActionType.EMBEDDED && (
                        <SquareCheck style={{ color: "green" }} />
                      )}
                    </div>

                    <span className="action-type">
                      {formatActionType(action.actionType)}
                    </span>

                    <span className="action-resource-title">
                      {!linkRecordId && actionTitle}
                    </span>

                    {action.actorDisplayName && (
                      <span className="action-actor-name">
                        {action.actorDisplayName}
                      </span>
                    )}

                    {action.actorAvatarUrl && (
                      <img
                        src={action.actorAvatarUrl}
                        alt="Actor Avatar"
                        className="action-actor-avatar"
                      />
                    )}

                    <span className="action-date">
                      {formatDate(action.createdAt)}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};
